import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ColumnDef, DateFilter, DateFilterMode } from './types';
import { RelativeDateInput } from './RelativeDateInput';

const MODE_LABELS: Record<DateFilterMode, string> = {
  eq: 'On',
  neq: 'Not on',
  gt: 'After',
  lt: 'Before',
  gte: 'On or after',
  lte: 'On or before',
  between: 'Between',
};

interface DateFilterEditorProps {
  value: DateFilter | undefined;
  onChange: (df: DateFilter | null) => void;
}

export function DateFilterEditor({ value, onChange }: DateFilterEditorProps) {
  const mode: DateFilterMode = value?.mode ?? 'gte';
  const v1 = value?.value ?? '';
  const v2 = value?.value2 ?? '';

  const setMode = (m: DateFilterMode) =>
    onChange({ mode: m, value: v1, value2: m === 'between' ? v2 : undefined });

  const setV1 = (s: string) => {
    if (!s && (mode !== 'between' || !v2)) {
      onChange(null);
      return;
    }
    onChange({ mode, value: s, value2: mode === 'between' ? v2 : undefined });
  };
  const setV2 = (s: string) => {
    if (!v1 && !s) {
      onChange(null);
      return;
    }
    onChange({ mode, value: v1, value2: s });
  };

  return (
    <div className="space-y-1.5">
      <Select value={mode} onValueChange={(m) => setMode(m as DateFilterMode)}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(MODE_LABELS) as DateFilterMode[]).map((m) => (
            <SelectItem key={m} value={m} className="text-xs">
              {MODE_LABELS[m]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {mode === 'between' ? (
        <div className="space-y-1">
          <RelativeDateInput value={v1} onChange={setV1} size="sm" />
          <RelativeDateInput value={v2} onChange={setV2} size="sm" />
        </div>
      ) : (
        <RelativeDateInput value={v1} onChange={setV1} size="sm" />
      )}
    </div>
  );
}

interface ColumnFilterControlsProps {
  column: ColumnDef;
  filterValues: Set<string> | undefined;
  dateFilter: DateFilter | undefined;
  uniqueValues: string[];
  onFilterChange: (values: Set<string>) => void;
  onDateFilterChange: (df: DateFilter | null) => void;
}

export function ColumnFilterControls({
  column,
  filterValues,
  dateFilter,
  uniqueValues,
  onFilterChange,
  onDateFilterChange,
}: ColumnFilterControlsProps) {
  const isDate = column.dataType === 'date';

  const toggleValue = (value: string) => {
    const next = new Set(filterValues ?? []);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onFilterChange(next);
  };

  return (
    <div>
      {isDate && (
        <div className="px-2 pt-2 pb-2 border-b border-stone-100 mb-1">
          <DateFilterEditor value={dateFilter} onChange={onDateFilterChange} />
        </div>
      )}
      {uniqueValues.length === 0
        ? !isDate && (
            <div className="px-3 py-6 text-sm text-stone-400 text-center">No values</div>
          )
        : uniqueValues.map((value) => {
            const checked = filterValues?.has(value) ?? false;
            return (
              <label
                key={value}
                className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-stone-50 rounded-sm"
              >
                <Checkbox checked={checked} onCheckedChange={() => toggleValue(value)} />
                <span className="text-sm text-stone-900 font-medium truncate">
                  {value || <span className="text-stone-400 italic">(empty)</span>}
                </span>
              </label>
            );
          })}
    </div>
  );
}
